import { useEffect, useMemo, useRef, useState } from "react";
import type { TaskChangeCommit, TaskChangeFile, TaskChangesDTO } from "@roomate/core";
import { useTaskChanges } from "../api.ts";
import { Pill, Tile } from "../calm.tsx";
import { changeBar, splitPatch, STATUS_LABEL, toSplitRows, type DiffLine, type FileDiff } from "../lib/diff.ts";
import { Ago, cx, ErrorNote } from "../ui.tsx";

/** Lines of one file shown before "Show more". */
export const COLLAPSED_LINES = 120;
const WIDE = "(min-width: 1024px)";

type View = "unified" | "split";

function useWide(): boolean {
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(WIDE).matches);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return wide;
}

const STATUS_TONE: Record<TaskChangeFile["status"], string> = {
  A: "text-[color:var(--ok)]",
  M: "text-ink-2",
  D: "text-high",
  R: "text-[color:var(--medium)]",
};

function ChangeBar({ file }: { file: TaskChangeFile }) {
  const bar = changeBar(file.insertions, file.deletions);
  const cells = [...Array(bar.add).fill("bg-[color:var(--ok)]"), ...Array(bar.del).fill("bg-high"), ...Array(bar.rest).fill("bg-ink/15")];
  return (
    <span className="inline-flex gap-px" aria-hidden>
      {cells.map((c, i) => (
        <span key={i} className={cx("h-2 w-2 rounded-[2px]", c)} />
      ))}
    </span>
  );
}

function Counts({ file }: { file: TaskChangeFile }) {
  if (file.binary) return <span className="text-ink-3">binary</span>;
  return (
    <span className="font-mono text-[12px]">
      <span className="text-[color:var(--ok)]">+{file.insertions}</span> <span className="text-high">−{file.deletions}</span>
    </span>
  );
}

const LINE_BG: Record<DiffLine["kind"], string> = {
  add: "bg-[color:var(--ok)]/10",
  del: "bg-high/10",
  ctx: "",
  hunk: "bg-ink/[0.04] text-ink-3",
};

function Code({ line }: { line: DiffLine }) {
  const sign = line.kind === "add" ? "+" : line.kind === "del" ? "−" : " ";
  return (
    <>
      <span className="select-none text-ink-3" aria-hidden>{sign}</span>
      {line.text}
    </>
  );
}

function UnifiedLines({ lines }: { lines: readonly DiffLine[] }) {
  return (
    <table className="w-full border-collapse font-mono text-[12px] leading-[1.55]" data-testid="diff-unified">
      <tbody>
        {lines.map((l, i) =>
          l.kind === "hunk" ? (
            <tr key={i} className={LINE_BG.hunk}>
              <td colSpan={3} className="px-3 py-0.5 whitespace-pre">{l.text}</td>
            </tr>
          ) : (
            <tr key={i} className={LINE_BG[l.kind]} data-line={l.kind}>
              <td className="w-10 select-none px-2 text-right text-ink-3">{l.oldNo ?? ""}</td>
              <td className="w-10 select-none px-2 text-right text-ink-3">{l.newNo ?? ""}</td>
              <td className="whitespace-pre pr-3"><Code line={l} /></td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

function SplitLines({ lines }: { lines: readonly DiffLine[] }) {
  const rows = toSplitRows(lines);
  const side = (l: DiffLine | undefined, no: number | undefined) => (
    <>
      <td className={cx("w-10 select-none px-2 text-right text-ink-3", l && LINE_BG[l.kind])}>{no ?? ""}</td>
      <td className={cx("w-1/2 whitespace-pre border-r border-rule/60 pr-3", l && LINE_BG[l.kind])}>{l && <Code line={l} />}</td>
    </>
  );
  return (
    <table className="w-full table-fixed border-collapse font-mono text-[12px] leading-[1.55]" data-testid="diff-split">
      <tbody>
        {rows.map((r, i) =>
          r.hunk ? (
            <tr key={i} className={LINE_BG.hunk}>
              <td colSpan={4} className="px-3 py-0.5 whitespace-pre">{r.hunk.text}</td>
            </tr>
          ) : (
            <tr key={i}>
              {side(r.left, r.left?.oldNo)}
              {side(r.right, r.right?.newNo)}
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

function FileBlock({ file, diff, open, onToggle, view, blockRef }: { file: TaskChangeFile; diff: FileDiff | undefined; open: boolean; onToggle: (open: boolean) => void; view: View; blockRef: (el: HTMLDetailsElement | null) => void }) {
  const [all, setAll] = useState(false);
  const lines = diff?.lines ?? [];
  const long = lines.length > COLLAPSED_LINES;
  const shown = long && !all ? lines.slice(0, COLLAPSED_LINES) : lines;
  return (
    <details ref={blockRef} open={open} onToggle={(e) => onToggle(e.currentTarget.open)} className="rounded-[14px] border border-rule bg-paper/60" data-testid="diff-file" data-path={file.path}>
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-[13px]">
        <span className={cx("inline-block transition-transform", open && "rotate-90")} aria-hidden>›</span>
        <span className="min-w-0 flex-1 truncate font-mono">{file.oldPath ? `${file.oldPath} → ${file.path}` : file.path}</span>
        <Counts file={file} />
      </summary>
      <div className="border-t border-rule">
        {file.binary ? (
          <p className="px-3 py-2.5 text-[12.5px] text-ink-2" data-testid="diff-binary">Binary file: no diff to show.</p>
        ) : !diff || lines.length === 0 ? (
          <p className="px-3 py-2.5 text-[12.5px] text-ink-2" data-testid="diff-omitted">
            {file.insertions + file.deletions === 0 ? (file.status === "R" ? "Renamed without changes." : "No content changes.") : "No diff was included for this file: the report hit its size cap."}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">{view === "split" ? <SplitLines lines={shown} /> : <UnifiedLines lines={shown} />}</div>
            {long && !all && (
              <button type="button" onClick={() => setAll(true)} className="w-full border-t border-rule px-3 py-2 text-left text-[12.5px] text-ink-2 hover:text-ink" data-testid="diff-show-more">
                Show {lines.length - COLLAPSED_LINES} more lines
              </button>
            )}
            {diff.clipped && (
              <p className="border-t border-rule px-3 py-2 text-[12.5px] text-ink-2" data-testid="diff-clipped">
                This file's diff was cut at the size cap; the rest is not shown.
              </p>
            )}
          </>
        )}
      </div>
    </details>
  );
}

function CommitRow({ c, merged }: { c: TaskChangeCommit; merged: boolean }) {
  return (
    <li className="flex items-baseline gap-3 py-1.5 text-[13px]" data-testid="change-commit">
      <code className="shrink-0 font-mono text-[12px] text-ink-2">{c.sha.slice(0, 7)}</code>
      <span className="min-w-0 flex-1 truncate">{c.subject}</span>
      {merged && <Pill tone="live" testId="change-merge-mark">merge</Pill>}
      <span className="hidden shrink-0 text-[12px] text-ink-3 sm:inline">{c.author}</span>
      <span className="shrink-0 text-[12px] text-ink-3">
        <Ago ts={c.at} />
      </span>
    </li>
  );
}

/** Pure body of the card, so tests can render it without the query layer. */
export function ChangesBody({ changes }: { changes: TaskChangesDTO }) {
  const { commits, files } = changes;
  const diffs = useMemo(() => splitPatch(changes.patch, files), [changes.patch, files]);
  const wide = useWide();
  const [view, setView] = useState<View>("unified");
  const effective: View = wide ? view : "unified";
  const [openPaths, setOpenPaths] = useState<Set<string>>(() => new Set(files.slice(0, 3).map((f) => f.path)));
  const refs = useRef(new Map<string, HTMLDetailsElement>());

  const jump = (path: string) => {
    setOpenPaths((s) => new Set(s).add(path));
    requestAnimationFrame(() => refs.current.get(path)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const ins = files.reduce((n, f) => n + f.insertions, 0);
  const del = files.reduce((n, f) => n + f.deletions, 0);

  return (
    <div className="space-y-5">
      <section>
        <h3 className="mb-1 text-[12.5px] font-medium text-ink-3">{commits.length} commit{commits.length === 1 ? "" : "s"}</h3>
        {commits.length > 0 ? (
          <ul className="divide-y divide-rule/60" data-testid="change-commits">
            {commits.map((c) => (
              <CommitRow key={c.sha} c={c} merged={!!changes.mergedSha && c.sha === changes.mergedSha} />
            ))}
          </ul>
        ) : (
          <p className="text-[13px] text-ink-3">{changes.mergedSha ? `Merged as ${changes.mergedSha.slice(0, 7)}.` : "No commit list in this report."}</p>
        )}
        {changes.mergedSha && !commits.some((c) => c.sha === changes.mergedSha) && (
          <p className="mt-1 text-[12.5px] text-ink-2" data-testid="change-merge-mark">
            <Pill tone="live">merged</Pill> as <code className="font-mono">{changes.mergedSha.slice(0, 7)}</code>
          </p>
        )}
      </section>

      <section>
        <div className="mb-1 flex items-baseline justify-between gap-3">
          <h3 className="text-[12.5px] font-medium text-ink-3">
            {files.length} file{files.length === 1 ? "" : "s"} <span className="font-mono"><span className="text-[color:var(--ok)]">+{ins}</span> <span className="text-high">−{del}</span></span>
          </h3>
          <div className="hidden gap-1 lg:flex" role="group" aria-label="Diff view" data-testid="diff-view-toggle">
            {(["unified", "split"] as const).map((v) => (
              <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)} data-testid={`diff-view-${v}`} className={cx("rounded-full px-2.5 py-0.5 text-[12px]", view === v ? "bg-ink text-paper" : "text-ink-2 hover:text-ink")}>
                {v === "unified" ? "Unified" : "Split"}
              </button>
            ))}
          </div>
        </div>
        <ul className="divide-y divide-rule/60" data-testid="change-files">
          {files.map((f) => (
            <li key={f.path} className="flex items-center gap-3 py-1.5 text-[13px]">
              <span className={cx("w-[4.5rem] shrink-0 text-[12px]", STATUS_TONE[f.status])} data-testid="change-file-status">{STATUS_LABEL[f.status]}</span>
              <button type="button" onClick={() => jump(f.path)} className="min-w-0 flex-1 truncate text-left font-mono text-[12.5px] hover:underline" data-testid="change-file-link">
                {f.oldPath ? `${f.oldPath} → ${f.path}` : f.path}
              </button>
              <Counts file={f} />
              <ChangeBar file={f} />
            </li>
          ))}
        </ul>
      </section>

      {changes.truncated && (
        <p className="rounded-[12px] bg-signal-soft/70 px-3 py-2 text-[12.5px] text-ink-2" data-testid="changes-truncated">
          The patch was truncated at the report's size cap, so some diffs are cut short or missing. The file list above is complete.
        </p>
      )}

      <section className="space-y-2" data-testid="change-diffs">
        {files.map((f) => (
          <FileBlock
            key={f.path}
            file={f}
            diff={diffs.get(f.path)}
            open={openPaths.has(f.path)}
            onToggle={(o) => setOpenPaths((s) => { const n = new Set(s); if (o) n.add(f.path); else n.delete(f.path); return n; })}
            view={effective}
            blockRef={(el) => (el ? refs.current.set(f.path, el) : refs.current.delete(f.path))}
          />
        ))}
      </section>
    </div>
  );
}

/** The task page's Changes card: what the task's branch (or merge commit) changed, from GET /tasks/:id/changes. */
export function TaskChanges({ taskId }: { taskId: string }) {
  const q = useTaskChanges(taskId);
  const c = q.data;
  const empty = !!c && c.commits.length === 0 && c.files.length === 0 && !c.mergedSha;
  return (
    <Tile span={6} title="Changes" aside={c && !empty ? <>reported <Ago ts={c.reportedAt} /></> : undefined} testId="task-changes">
      {q.error ? (
        <ErrorNote error={q.error} />
      ) : !c ? (
        <p className="text-[13px] text-ink-3">Fetching changes…</p>
      ) : empty ? (
        <p className="text-[13px] text-ink-3" data-testid="changes-empty">No commits yet. They appear here once the builder's branch has work on it.</p>
      ) : (
        <ChangesBody changes={c} />
      )}
    </Tile>
  );
}
